import { formSlugs } from "@/helpers/utils/form-constants";
import { STARTUP_ALPHA_CLOSES_AT } from "@/helpers/utils/startup-alpha-constants";

export type ApplicationCycleStatus = "unannounced" | "open" | "closed" | "archived";

export type ApplicationCycle = {
  formKey: string;
  eventKey: string;
  cycleKey: string;
  festivalYear: number | null;
  status: ApplicationCycleStatus;
  opensAt: string | null;
  closesAt: string | null;
  timezone: string;
};

export const CURRENT_FESTIVAL_YEAR = 2027;
export const FESTIVAL_EVENT_KEY = "festival-2027";
const EDMONTON_TZ = "America/Edmonton";

function festival(formKey: string, status: ApplicationCycleStatus = "open"): ApplicationCycle {
  return {
    formKey,
    eventKey: FESTIVAL_EVENT_KEY,
    cycleKey: `${formKey}:${FESTIVAL_EVENT_KEY}`,
    festivalYear: CURRENT_FESTIVAL_YEAR,
    status,
    opensAt: null,
    closesAt: null,
    timezone: EDMONTON_TZ,
  };
}

function standalone(
  formKey: string,
  eventKey: string,
  status: ApplicationCycleStatus = "open"
): ApplicationCycle {
  return {
    formKey,
    eventKey,
    cycleKey: `${formKey}:${eventKey}`,
    festivalYear: null,
    status,
    opensAt: null,
    closesAt: null,
    timezone: EDMONTON_TZ,
  };
}

const s = formSlugs;
const FILM_OPENS_AT = "2026-12-01T00:00:00-07:00";

export const FORM_CYCLES: Record<string, ApplicationCycle> = {
  [s.techElevate]: festival(s.techElevate),
  [s.techPitchElevateHer]: festival(s.techPitchElevateHer),
  [s.techPitchCBlack]: festival(s.techPitchCBlack),
  [s.techSpeaker]: festival(s.techSpeaker),
  [s.techInvestor]: festival(s.techInvestor),
  [s.techEngineers]: festival(s.techEngineers),
  [s.techResponsibleDeepTech]: festival(s.techResponsibleDeepTech),
  [s.techCBlackSemiFinals]: festival(s.techCBlackSemiFinals),
  [s.techCElevateHerSemiFinals]: festival(s.techCElevateHerSemiFinals),
  // Owner decision 2026-10-26: rolling, closes May 15, 2027, end of day in Edmonton.
  [s.techStartupAlpha]: { ...festival(s.techStartupAlpha), closesAt: STARTUP_ALPHA_CLOSES_AT },

  [s.gamingEsportOverwatch]: {
    ...festival(s.gamingEsportOverwatch),
    // Owner decision 2026-10-16: registration closes four weeks before the festival.
    closesAt: "2027-07-15T23:59:59-06:00",
  },
  [s.gamingSpeaker]: festival(s.gamingSpeaker),
  [s.gamingDeveloper]: festival(s.gamingDeveloper),
  [s.gamingPitchQuest]: festival(s.gamingPitchQuest),
  [s.gamingPlayTestZone]: festival(s.gamingPlayTestZone),
  // CTJAM registration (2026-11-04). TODO(confirm): closing date once the jam days are set.
  [s.gamingCtjam]: festival(s.gamingCtjam),

  [s.fashionDesigner]: festival(s.fashionDesigner),
  [s.fashionBuyer]: festival(s.fashionBuyer),
  [s.fashionStylist]: festival(s.fashionStylist),
  [s.fashionMua]: festival(s.fashionMua),
  [s.fashionModel]: festival(s.fashionModel),
  [s.fashionProfessionals]: festival(s.fashionProfessionals),
  [s.fashionExhibit]: festival(s.fashionExhibit),
  [s.fashionPartner]: festival(s.fashionPartner),

  [s.musicArtist]: festival(s.musicArtist),
  [s.musicPublisher]: festival(s.musicPublisher),
  [s.musicPublisherPublisher]: festival(s.musicPublisherPublisher),

  [s.vendor]: festival(s.vendor),
  // The server classifies vendor submissions after June 25 as waitlist-only.
  [s.villageInterest]: festival(s.villageInterest),
  // Public resume collection stays closed until protected storage and scoped
  // named-employer approval are implemented and verified.
  [s.resumeUpload]: festival(s.resumeUpload, "closed"),
  [s.volunteer]: festival(s.volunteer),
  [s.b2b]: festival(s.b2b),
  [s.influencer]: festival(s.influencer),
  [s.photographer]: festival(s.photographer),
  [s.videographer]: festival(s.videographer),
  [s.diversityPass]: festival(s.diversityPass),
  [s.generalSpeaker]: festival(s.generalSpeaker),
  [s.innovationZone]: festival(s.innovationZone),
  // Legacy film form: closed to new writes; /form/film renders a chooser
  // for the three forms below. Existing responses are untouched.
  [s.film]: festival(s.film, "closed"),
  // Owner decisions 2026-10-20: open December 1, 2026 (MST). Submissions
  // and pitches close April 30, 2027; industry closes July 15, 2027 (MDT).
  [s.filmSubmission]: { ...festival(s.filmSubmission), opensAt: FILM_OPENS_AT, closesAt: "2027-04-30T23:59:59-06:00" },
  [s.filmPitch]: { ...festival(s.filmPitch), opensAt: FILM_OPENS_AT, closesAt: "2027-04-30T23:59:59-06:00" },
  [s.filmIndustry]: { ...festival(s.filmIndustry), opensAt: FILM_OPENS_AT, closesAt: "2027-07-15T23:59:59-06:00" },
  [s.artist]: festival(s.artist),
  [s.health]: festival(s.health),

  // Standing intakes and standalone events: no festival year is written.
  [s.fashionModelNovember2026]: {
    // Keep the original identity so rescheduling does not orphan applications.
    ...standalone(s.fashionModelNovember2026, "fashion-show-2026-11"),
    closesAt: "2027-01-11T23:59:59-07:00",
  },
  [s.fashionDesignerFebruary2027]: {
    ...standalone(s.fashionDesignerFebruary2027, "fashion-show-2026-11"),
    closesAt: "2026-12-14T23:59:59-07:00",
  },
  // Photographers for the same February 25 show. No closing date set yet.
  [s.fashionPhotographerFebruary2027]: standalone(s.fashionPhotographerFebruary2027, "fashion-show-2026-11"),
  [s.mediaPress]: standalone(s.mediaPress, "media-press"),
  [s.partnership]: standalone(s.partnership, "partnership"),
  [s.vipDinner]: standalone(s.vipDinner, "vip-dinner"),
  [s.techExecLuncheonGeneralAdmission2k25]: standalone(
    s.techExecLuncheonGeneralAdmission2k25,
    "tech-exec-luncheon-2025",
    "closed"
  ),
};

export function resolveFormKey(formKey: string): string {
  if (formKey === "village-updates") return s.villageInterest;
  return formKey === "fashion/model-february-2027"
    ? s.fashionModelNovember2026
    : formKey;
}

export function getFormCycle(formKey: string): ApplicationCycle | undefined {
  return FORM_CYCLES[resolveFormKey(formKey)];
}

// A cycle accepts submissions only while its status is "open" and `now` falls
// inside its opensAt/closesAt window (either bound may be null = unbounded).
export function isCycleOpen(cycle: ApplicationCycle, now: Date = new Date()): boolean {
  if (cycle.status !== "open") return false;
  const start = cycle.opensAt === null ? -Infinity : Date.parse(cycle.opensAt);
  const end = cycle.closesAt === null ? Infinity : Date.parse(cycle.closesAt);
  if (Number.isNaN(start) || Number.isNaN(end) || start > end) return false;
  if (!Number.isFinite(now.getTime()) || now.getTime() < start || now.getTime() > end) return false;
  return true;
}

// For an open cycle whose window has not started yet, the opening date as
// shown to applicants (e.g. "December 1, 2026"); otherwise null.
export function getCycleOpensOn(cycle: ApplicationCycle, now: Date = new Date()): string | null {
  if (cycle.status !== "open" || cycle.opensAt === null) return null;
  const start = Date.parse(cycle.opensAt);
  if (Number.isNaN(start) || now.getTime() >= start) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: cycle.timezone, year: "numeric", month: "long", day: "numeric",
  }).format(new Date(start));
}

export function getCycleDocumentId(cycle: ApplicationCycle): string {
  const formId = cycle.formKey.replaceAll("/", "_");
  return cycle.festivalYear === null ? formId : `${formId}_${cycle.festivalYear}`;
}
